import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Search, X, MapPin } from "lucide-react";

const SearchableSelect = ({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  disabled = false,
  error = "",
  label = "",
  icon: Icon = MapPin,
  onSearch,
  showOtherOption = false,
  otherOptionLabel = "Other (Custom Location)",
  emptyMessage = "No options found",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter options based on search
  const filteredOptions = searchQuery
    ? options.filter((option) =>
        option.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : options;

  // Show "Other" option when search has no results or when showOtherOption is true
  const shouldShowOther = showOtherOption && (filteredOptions.length === 0 || searchQuery);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset highlighted index when options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [filteredOptions.length]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    const totalOptions = shouldShowOther
      ? filteredOptions.length + 1
      : filteredOptions.length;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < totalOptions - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[highlightedIndex]);
        } else if (shouldShowOther) {
          handleSelect("__other__");
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery("");
        break;
      default:
        break;
    }
  };

  const handleSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchQuery("");
    setHighlightedIndex(0);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSearchQuery("");
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (onSearch) {
      onSearch(query);
    }
  };

  const displayValue = value === "__other__" ? otherOptionLabel : value;

  return (
    <div ref={dropdownRef} className="relative">
      {label && (
        <label className="block mb-2 text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {/* Select Button */}
      <motion.button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        whileHover={!disabled ? { scale: 1.01 } : {}}
        whileTap={!disabled ? { scale: 0.99 } : {}}
        className={`relative w-full px-3 py-3 pl-10 pr-10 text-left bg-white border rounded-lg transition-all duration-200 flex items-center justify-between ${
          disabled
            ? "text-gray-500 bg-gray-100 border-gray-300 cursor-not-allowed"
            : error
            ? "border-red-500 focus:ring-2 focus:ring-red-500 focus:border-transparent"
            : isOpen
            ? "border-red-500 ring-2 ring-red-500 ring-opacity-50"
            : "border-gray-300 hover:border-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent"
        }`}
      >
        <Icon
          className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none flex-shrink-0 ${
            disabled ? "text-gray-400" : "text-gray-500"
          }`}
        />

        <span
          className={`flex-1 truncate ${
            value ? "text-gray-900" : "text-gray-500"
          }`}
        >
          {displayValue || placeholder}
        </span>

        <div className="flex items-center space-x-1">
          {value && !disabled && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              onClick={handleClear}
              className="p-1 text-gray-400 rounded-full hover:text-gray-600 hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </motion.div>
          )}
          <ChevronDown
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? "transform rotate-180" : ""}`}
           />
         </div>
       </motion.button>

      {/* Error Message */}
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 text-sm text-red-500"
        >
          {error}
        </motion.p>
      )}

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden absolute z-50 mt-2 w-full bg-white rounded-lg border border-gray-200 shadow-2xl"
          >
            {/* Search Input */}
            <div className="p-3 bg-gray-50 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Search..."
                  className="py-2 pr-4 pl-10 w-full text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  autoFocus
                />
              </div>
            </div>

            {/* Options List */}
            <div className="overflow-y-auto max-h-60 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
              {filteredOptions.length === 0 && !shouldShowOther ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-gray-500">{emptyMessage}</p>
                  {showOtherOption && (
                    <p className="mt-1 text-xs text-gray-400">
                      Select "Other" to enter a custom location
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {filteredOptions.map((option, index) => (
                    <motion.button
                      key={option}
                      type="button"
                      onClick={() => handleSelect(option)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors duration-150 ${
                        highlightedIndex === index
                          ? "bg-red-50 text-red-700"
                          : value === option
                          ? "bg-gray-100 text-gray-900 font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span>{option}</span>
                        {value === option && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-red-600"
                          >
                            ✓
                          </motion.div>
                        )}
                      </div>
                    </motion.button>
                  ))}

                  {/* Other Option */}
                  {shouldShowOther && (
                    <motion.button
                      key="__other__"
                      type="button"
                      onClick={() => handleSelect("__other__")}
                      onMouseEnter={() =>
                        setHighlightedIndex(filteredOptions.length)
                      }
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={`w-full px-4 py-3 text-left text-sm transition-colors duration-150 border-t border-gray-200 ${
                        highlightedIndex === filteredOptions.length
                          ? "bg-orange-50 text-orange-700"
                          : value === "__other__"
                          ? "bg-gray-100 text-gray-900 font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="flex items-center space-x-2">
                          <span className="text-orange-600">✏️</span>
                          <span>{otherOptionLabel}</span>
                        </span>
                        {value === "__other__" && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-orange-600"
                          >
                            ✓
                          </motion.div>
                        )}
                      </div>
                    </motion.button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchableSelect;
